import { Tooltip, TooltipContent, TooltipTrigger } from '@cp/src/components/TooltipV2/TooltipV2';
import { ActivityTypeLabels, EXPECTED_DURATION_IN_MINUTES, LoginStatus } from './constants';
import { ActivityItem } from './types';
import {
  calculateActivityBarWidths,
  calculateTargetPosition,
  convertTimeToMinutes,
  getActivityColorClassMap,
  isLateLogin
} from './utils';
import Typography from '@navi/web-ui/lib/primitives/Typography/Typography';
import { DateFormat, getFormatDate } from '@cp/src/utils/DateHelper';
import TargetBarIcon from '@cp/src/assets/icons/TargetBarIcon';
import cx from 'classnames';

const renderActivityBar = ({
  activities = [],
  loginTimeStamp,
  showLoginTime = false,
  showExpectedDuration = false,
  expectedDurationInMinutes = EXPECTED_DURATION_IN_MINUTES,
  loginStatus,
  totalExpectedMinutes
}: {
  activities: ActivityItem[];
  loginTimeStamp?: number;
  showLoginTime?: boolean;
  showExpectedDuration?: boolean;
  expectedDurationInMinutes?: number;
  loginStatus?: LoginStatus;
  totalExpectedMinutes: number;
}) => {
  const calculatedActivities = calculateActivityBarWidths(activities, totalExpectedMinutes);

  const targetPositionPercentage = calculateTargetPosition(
    expectedDurationInMinutes,
    calculatedActivities
  );

  const isLateLogin = loginStatus === LoginStatus.LATE_LOGGED_IN;

  return (
    <div className="flex flex-row items-start relative">
      {showLoginTime ? (
        <Tooltip placement="top" hideStrategy="referenceHidden">
          <div>
            <TooltipTrigger asChild>
              <div
                className={`w-[3px] h-[28px] !rounded-sm absolute mt-[-2px]`}
                style={{
                  backgroundColor: isLateLogin
                    ? getActivityColorClassMap['lateLogin']
                    : getActivityColorClassMap['inTimeLogin']
                }}
              />
            </TooltipTrigger>
          </div>
          <TooltipContent
            className={cx('tooltipWrapper', 'flex flex-col items-center justify-center')}
          >
            <Typography variant="p5" color="var(--grayscale-3)">
              Login time
            </Typography>
            <Typography variant="p5" color="var(--bg-primary)">
              {loginTimeStamp ? getFormatDate(loginTimeStamp, DateFormat.hh_mm_ss_ampm) : '-'}
            </Typography>
          </TooltipContent>
        </Tooltip>
      ) : null}
      <div className="w-full h-6 flex rounded-md overflow-hidden border border-[#E0E0E0]">
        {calculatedActivities.map((activity, index) => {
          if (activity.type === 'empty') {
            return (
              <div
                key={`empty-${index}`}
                style={{
                  width: `${activity.durationInPercentage}%`,
                  backgroundColor: getActivityColorClassMap['empty'],
                  height: '100%'
                }}
              />
            );
          }

          return (
            <Tooltip
              key={`activity-${index}`}
              placement="top"
              hideStrategy="referenceHidden"
              hiddenPadding={0}
            >
              <div style={{ width: `${activity.durationInPercentage}%`, height: '100%' }}>
                <TooltipTrigger asChild>
                  <div
                    style={{
                      backgroundColor:
                        getActivityColorClassMap[
                          (activity.type as keyof typeof getActivityColorClassMap) ?? 'empty'
                        ],
                      width: '100%',
                      height: '100%'
                    }}
                  />
                </TooltipTrigger>
              </div>
              <TooltipContent
                className={cx('tooltipWrapper', 'flex flex-col items-center justify-center')}
              >
                <Typography variant="p5" color="var(--grayscale-3)">
                  {
                    ActivityTypeLabels[activity.type as keyof typeof ActivityTypeLabels]
                      .contentInTip
                  }
                </Typography>
                <Typography variant="p5" color="var(--bg-primary)">
                  {convertTimeToMinutes(activity.durationInMinutes)}
                </Typography>
              </TooltipContent>
            </Tooltip>
          );
        })}

        {showExpectedDuration && (
          <Tooltip placement="top" hideStrategy="referenceHidden">
            <div
              className="absolute"
              style={{
                left: `${targetPositionPercentage}%`,
                top: '-6px',
                zIndex: 10
              }}
            >
              <TooltipTrigger>
                <TargetBarIcon />
              </TooltipTrigger>
            </div>
            <TooltipContent
              className={cx(
                'tooltipWrapper',
                '!top-[-20px] flex flex-col justify-center items-center'
              )}
            >
              <Typography variant="p5" color="var(--grayscale-3)">
                Target
              </Typography>
              <Typography variant="p5" color="var(--bg-primary)">
                {convertTimeToMinutes(expectedDurationInMinutes)}
              </Typography>
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    </div>
  );
};

export default renderActivityBar;
